import { useEffect, useState, useCallback } from "react";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogTitle, DialogHeader, DialogFooter
} from "@/components/ui/dialog";
import { Plus, Trash2, Inbox } from "lucide-react";
import { toast } from "sonner";

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ method: "Cash", amount: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    const [p, i] = await Promise.all([api.get("/payments"), api.get("/invoices")]);
    setPayments(p.data);
    setInvoices(i.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onInvoiceSelect = (id) => {
    const inv = invoices.find((x) => x.id === id);
    if (inv) setForm({
      ...form,
      invoice_id: id,
      customer_id: inv.customer_id,
      customer_name: inv.customer_name,
      amount: Math.max(0, (inv.grand_total || 0) - (inv.amount_paid || 0)),
    });
  };

  const save = async () => {
    if (!form.amount || form.amount <= 0) return toast.error("Enter amount");
    try {
      await api.post("/payments", form);
      toast.success("Payment recorded");
      setOpen(false);
      setForm({ method: "Cash", amount: 0 });
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete payment?")) return;
    await api.delete(`/payments/${id}`);
    load();
  };

  const total = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const pendingInvs = invoices.filter((i) => i.status !== "paid");
  const outstanding = pendingInvs.reduce((s, i) => s + ((i.grand_total || 0) - (i.amount_paid || 0)), 0);

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Payments</h1>
          <p className="text-sm text-muted-foreground mt-1">Record payments received against invoices</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="record-payment-btn"><Plus size={16} className="mr-1" /> Record Payment</Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Total Collected</div><div className="font-heading font-bold text-2xl mt-1">{inr(total)}</div></Card>
        <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Outstanding</div><div className="font-heading font-bold text-2xl mt-1 text-orange-600">{inr(outstanding)}</div></Card>
        <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Pending Invoices</div><div className="font-heading font-bold text-2xl mt-1">{pendingInvs.length}</div></Card>
      </div>

      <Card className="overflow-hidden">
        {loading ? <div className="p-10 text-center text-sm">Loading…</div> : payments.length === 0 ? (
          <div className="p-16 text-center"><Inbox size={36} className="mx-auto text-muted-foreground" /><div className="mt-4 text-sm text-muted-foreground">No payments yet.</div></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{(p.payment_date || p.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="font-medium">{p.customer_name || "—"}</TableCell>
                  <TableCell><Badge variant="secondary">{p.method}</Badge></TableCell>
                  <TableCell>{p.reference || "—"}</TableCell>
                  <TableCell className="font-semibold">{inr(p.amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => remove(p.id)} className="text-destructive"><Trash2 size={14} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="grid gap-4 mt-2">
            <div>
              <Label>Invoice</Label>
              <select className="w-full mt-1 h-10 rounded-lg border bg-background px-3 text-sm" onChange={(e) => onInvoiceSelect(e.target.value)} data-testid="payment-invoice">
                <option value="">— Select invoice (optional) —</option>
                {pendingInvs.map((i) => <option key={i.id} value={i.id}>{i.invoice_number} · {i.customer_name} · {inr((i.grand_total||0) - (i.amount_paid||0))} due</option>)}
              </select>
            </div>
            <div>
              <Label>Customer Name</Label>
              <Input value={form.customer_name || ""} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} data-testid="payment-amount" />
              </div>
              <div>
                <Label>Method</Label>
                <select className="w-full mt-1 h-10 rounded-lg border bg-background px-3 text-sm" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="payment-method">
                  {["Cash", "UPI", "Cheque", "Card", "Bank Transfer"].map((m) => <option key={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Reference / Note</Label>
              <Input value={form.reference || ""} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="payment-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
